'use client'
import styles from './PageTopNav.module.scss'
import useWindowSize from '@/src/hooks/useWindowSize'
import { JSX, useCallback, useContext, useEffect, useRef, useState } from 'react'
import SVGBurgerMenu from '@/src/components/common/SVG/SVGBurgerMenu'
import { classNames } from '@/src/helpers/component'
import Link from 'next/link'
import SVGClose from '@/src/components/common/SVG/SVGClose'
import PageGutter from '@/src/components/common/PageGutter'
import usePageLoad from '@/src/hooks/usePageLoad'
import SVGChevron from '@/src/components/common/SVG/SVGChevron'
import { Block_Top_Nav, Cta_Button, Maybe } from '@/src/generated/graphql'
import { convertLinkGroupToMenuItems } from '@/src/helpers/link-group'
import CTAButton from '@/src/components/common/CTAButton'
import AGLogo from '@/src/components/common/AGLogo'
import PlaceholderTopNav from '@/src/components/common/PlaceholderTopNav'
import { PageContext } from '@/src/contexts/PageContext'

export default function PageTopNav(_: Block_Top_Nav) {
  const pageContext = useContext(PageContext)
  const [shownBurgerMenu, setShownBurgerMenu] = useState(false)
  const [shownModalMenu, setShownModalMenu] = useState(false)
  const [openMenus, setOpenMenus] = useState<boolean[]>([])
  const ref = useRef<HTMLDivElement>(null)
  const refWrapper = useRef<HTMLDivElement>(null)
  const refLeftNav = useRef<HTMLDivElement>(null)
  const refRightNav = useRef<HTMLDivElement>(null)
  const dialogRef = useRef<HTMLDialogElement>(null)

  const closeModalMenu = () => {
    setOpenMenus([])
    setShownModalMenu(false)
  }

  const handleWindowSize = useCallback(() => {
    const navLeftRect = refLeftNav.current?.getBoundingClientRect()
    if (!navLeftRect) return

    const navRightRect = refRightNav.current?.getBoundingClientRect()
    if (!navRightRect) return

    const navWrapperRect = refWrapper.current?.getBoundingClientRect()
    if (!navWrapperRect) return

    const isTight = Math.floor(navRightRect.right) > Math.floor(navWrapperRect.right)
    setShownBurgerMenu(isTight)
    if (!isTight) setShownModalMenu(false)
  }, [])

  useWindowSize(handleWindowSize)

  const updateContainerHeight = useCallback(() => {
    const container = ref.current
    if (!container) return
    pageContext?.topnavHeightState[1](container.clientHeight + 20)
  }, [pageContext?.topnavHeightState])

  usePageLoad(() => {
    handleWindowSize()
    updateContainerHeight()
  })

  useEffect(() => {
    const container = ref.current
    if (!container) return

    const observer = new ResizeObserver(() => {
      updateContainerHeight()
    })

    observer.observe(container)

    return () => {
      observer.disconnect()
    }
  }, [updateContainerHeight])

  useEffect(() => {
    if (shownModalMenu) {
      dialogRef.current?.showModal()
    } else {
      setTimeout(() => {
        dialogRef.current?.close()
      }, 500) // allows dialog close animation to play before actually closing
    }
  }, [shownModalMenu])

  function renderButtons(ctaButtons: Maybe<Cta_Button>[]): JSX.Element | null {
    return (
      <div className={styles.buttonsContainer}>
        {ctaButtons.map((_, idx) =>
          _ ? (
            <CTAButton
              key={idx}
              {..._}
              onClick={() => {
                closeModalMenu()
              }}
            />
          ) : null,
        )}
      </div>
    )
  }

  const menuItems = convertLinkGroupToMenuItems(_.link_groups ?? [])

  return (
    <nav>
      <PlaceholderTopNav ref={ref}>
        <PageGutter className={styles.wrapper} ref={refWrapper}>
          <AGLogo />
          <div className={styles.navContentBurger}>
            <div className={classNames(styles.navContent, shownBurgerMenu ? styles.hidden : '')}>
              <div className={styles.left} ref={refLeftNav}>
                <ul className={styles.navmenuContainer}>
                  {Object.keys(menuItems).map((label, idx) => {
                    return (
                      <li key={label} className={styles.menuGroup}>
                        <button
                          id={`top-menu-button-${idx}`}
                          className={styles.menuLabel}
                          type="button"
                          aria-controls={`top-menu-${idx}`}
                          aria-expanded={openMenus[idx]}>
                          {label}
                          <SVGChevron direction={'down'} />
                        </button>
                        <ul
                          className={styles.menuOptions}
                          id={`top-menu-${idx}`}
                          aria-labelledby={`top-menu-button-${idx}`}>
                          {Object.keys(menuItems[label]).map(text => (
                            <li key={text}>
                              <Link
                                href={menuItems[label][text]}
                                aria-hidden={!shownBurgerMenu}
                                tabIndex={shownBurgerMenu ? -1 : 0}>
                                {text}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </li>
                    )
                  })}
                </ul>
              </div>
              <div className={styles.right} ref={refRightNav}>
                {renderButtons(_.cta_buttons ?? [])}
              </div>
            </div>

            {shownBurgerMenu && (
              <button
                onClick={() => setShownModalMenu(true)}
                aria-label="Open navigation menu"
                type="button"
                className={styles.burgerContainer}>
                <SVGBurgerMenu className={styles.burger} />
              </button>
            )}
          </div>
        </PageGutter>
      </PlaceholderTopNav>
      <dialog
        ref={dialogRef}
        onCancel={closeModalMenu}
        aria-label="Navigation Menu"
        className={styles.modalContainer}>
        <div className={classNames(styles.modal, shownModalMenu ? 'modal-shown' : 'modal-hidden')}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <AGLogo />
              <button
                aria-label="Close navigation menu"
                onClick={closeModalMenu}
                type="button"
                className={styles.modalCloseContainer}>
                <SVGClose className={styles.modalClose} />
              </button>
            </div>
            <ul className={styles.modalBody}>
              {Object.keys(menuItems).map((label, idx) => {
                return (
                  <li
                    key={label}
                    className={classNames(styles.menuGroup, openMenus[idx] ? styles.open : '')}>
                    <button
                      type="button"
                      id={`menu-button-${idx}`}
                      className={styles.menuLabel}
                      onClick={() => {
                        openMenus[idx] = !openMenus[idx]
                        setOpenMenus([...openMenus])
                      }}
                      aria-controls={`menu-${idx}`}
                      aria-expanded={openMenus[idx]}>
                      {label}
                      <SVGChevron direction={'down'} />
                    </button>
                    <ul
                      className={styles.menuOptions}
                      id={`menu-${idx}`}
                      aria-labelledby={`menu-button-${idx}`}>
                      {Object.keys(menuItems[label]).map(text => (
                        <li
                          key={text}
                          className={classNames(!openMenus[idx] ? 'display-none' : '')}>
                          <Link href={menuItems[label][text]} onClick={closeModalMenu}>
                            {text}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </li>
                )
              })}
            </ul>
            <div className={styles.modalFooter}>{renderButtons(_.cta_buttons ?? [])}</div>
          </div>
        </div>
      </dialog>
    </nav>
  )
}
